import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PaymentProvider } from './components/payment';
import { ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import LoginForm from './components/common/LoginForm';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import RegisterForm from './components/common/RegisterForm';
import RootRedirect from './components/common/RootRedirect';
import VersionWatcher from './components/common/VersionWatcher';
import HomePage from './components/HomePage';
import Home from './pages/Home';
import VerifyEmail from './pages/VerifyEmail';
import TourCatalog from './components/customer/TourCatalog';
import CustomerBookingFlow from './components/customer/BookingFlow';
import BookingConfirmation from './components/customer/BookingConfirmation';
import BookingPaymentSuccess from './components/customer/BookingPaymentSuccess';
import CalendarBooking from './components/booking/CalendarBooking';
import BookingFlow from './components/booking/BookingFlow';
import UserBookingCalendar from './components/booking/UserBookingCalendar';
import ImprovedBookingCalendar from './components/customer/ImprovedBookingCalendar';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import BookingManagement from './components/admin/BookingManagement';
import CustomerManagement from './components/admin/CustomerManagement';
import CalendarManagement from './components/admin/CalendarManagement';
import AddOnManagement from './components/admin/AddOnManagement';
import TourTypeAddonConfig from './components/admin/TourTypeAddonConfig';
import TourTypeManagement from './components/admin/TourTypeManagement';
// T-046 (11.09.2026): PaymentOverview und Reports ausgeblendet (Prototypen), Komponenten bleiben im Repo
import InvoiceManagement from './components/admin/InvoiceManagement';
// Barrechnungen und Bordverkäufe (Anlage v1.4 Abschnitt 2.7 Punkt 10)
import CashInvoiceManagement from './components/admin/CashInvoiceManagement';
import BankReconciliation from './components/admin/BankReconciliation';
import Settings from './components/admin/Settings';
import VoucherManagement from './components/admin/VoucherManagement';
import QuoteManagement from './components/admin/QuoteManagement';
import EmailQueueManagement from './components/admin/EmailQueueManagement';
import UserDashboard from './components/user/UserDashboard';
import Checkout from './components/payment/Checkout';
import PaymentSuccess from './components/payment/PaymentSuccess';
import { VoucherPurchase, VoucherSuccess, VoucherBankTransfer } from './pages/voucher';
import QuoteAcceptPage from './pages/QuoteAcceptPage';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode, 
  requiredRole?: 'Admin' | 'Staff' | 'Customer' 
}> = ({ children, requiredRole }) => {
  const { isAuthenticated, user, isAdmin, isStaff, isLoading } = useAuth();
  
  console.log('ProtectedRoute check:', { 
    isAuthenticated, 
    user, 
    requiredRole, 
    isLoading,
    userRole: user?.Role || user?.role,
    isAdminResult: requiredRole === 'Admin' ? isAdmin() : null
  });

  // Wait for auth to initialize
  if (isLoading) {
    console.log('Auth is still loading, showing spinner...');
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'Admin') {
    // Direct role check - Quick Fix for JWT role issue
    // Buchhaltungsrolle (Anlage v1.4 Abschnitt 2.7 Punkt 9): darf in den Admin-Bereich,
    // sieht dort aber nur Rechnungen und Zahlungen (Filterung in AdminLayout)
    const userRole = user?.Role || user?.role;
    const isAdminUser = userRole === 'Admin' || userRole === 3; // 3 = Admin role number
    const isAccountingUser = userRole === 'Accounting' || userRole === 5; // 5 = Accounting role number
    console.log('Admin role check - Direct:', {
      userRole,
      isAdminUser,
      isAccountingUser,
      userObject: user,
      roleFromUser: user?.Role,
      roleFromUserLowercase: user?.role
    });

    if (!isAdminUser && !isAccountingUser) {
      console.log('User is not admin (direct check), redirecting to home');
      return <Navigate to="/" replace />;
    }
  }

  if (requiredRole === 'Staff') {
    // Direct role check - Quick Fix
    const userRole = user?.Role || user?.role;
    const isStaffOrAdmin = userRole === 'Staff' || userRole === 'Admin' || userRole === 2 || userRole === 3; // 2=Staff, 3=Admin
    console.log('Staff role check - Direct:', {
      userRole,
      isStaffOrAdmin
    });
    
    if (!isStaffOrAdmin) {
      return <Navigate to="/" replace />;
    }
  }

  console.log('ProtectedRoute: Access granted');
  return <>{children}</>;
};

// T-115 (v1.8.31, Review Befund 2): Kalender beim Wechsel angemeldet/abgemeldet neu aufbauen, damit nach dem
// Abmelden auf /booking keine Verwaltungsdaten (alle Termine) in der öffentlichen Ansicht stehen bleiben
const BookingCalendarRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return <ImprovedBookingCalendar key={isAuthenticated ? 'auth' : 'anon'} />;
};

// Main Navigation Component
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  
  // Direct role check - Quick Fix
  const userRole = user?.Role || user?.role;
  const canAccessAdmin = userRole === 'Admin' || userRole === 'Staff' || userRole === 3 || userRole === 2; // 3=Admin, 2=Staff
  
  console.log('MainLayout role check:', {
    userRole,
    canAccessAdmin,
    user
  });

  // Wait for auth to initialize
  if (isLoading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) {
    // T-115 (v1.8.31): öffentliche Terminansicht (/booking) mit schlanker Kopfzeile Anmelden/Registrieren.
    // Alle anderen Seiten mit MainLayout liegen hinter ProtectedRoute und erreichen diesen Zweig nicht.
    return (
      <div className="app-main">
        <header className="app-header">
          <div className="header-content">
            <div className="header-title">
              <img
                src="/logo-frankenfloss.svg"
                alt="Frankenfloss Logo"
                className="header-logo"
              />
              <h1>Buchungskalender</h1>
            </div>
            <nav className="main-navigation">
              <a href="/login" className="nav-tab">
                <span className="nav-text">Anmelden</span>
              </a>
              <a href="/register" className="nav-tab">
                <span className="nav-text">Registrieren</span>
              </a>
            </nav>
          </div>
        </header>
        <main className="app-content">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="app-main">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <img 
              src="/logo-frankenfloss.svg" 
              alt="Frankenfloss Logo" 
              className="header-logo"
            />
            <h1>Buchungssystem</h1>
          </div>
          
          <nav className="main-navigation">
            <a href="/booking" className="nav-tab">
              <span className="nav-icon">📅</span>
              <span className="nav-text">Buchungskalender</span>
            </a>
            <a href="/dashboard" className="nav-tab">
              <span className="nav-icon">👤</span>
              <span className="nav-text">Mein Konto</span>
            </a>
            {canAccessAdmin && (
              <a href="/admin" className="nav-tab">
                <span className="nav-icon">🛠️</span>
                <span className="nav-text">Admin Dashboard</span>
              </a>
            )}
          </nav>
          
          <div className="user-menu">
            <div className="user-info">
              <span className="user-name">
                {user?.firstName} {user?.lastName}
              </span>
              {canAccessAdmin && <span className="admin-badge">Admin</span>}
            </div>
            <button onClick={logout} className="logout-button">
              <ArrowLeftOnRectangleIcon className="logout-icon" />
              <span className="logout-text">Abmelden</span>
            </button>
          </div>
        </div>
      </header>

      <main className="app-content">
        {children}
      </main>
    </div>
  );
};

// Auth Pages Container
const AuthContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="auth-container">
      {children}
    </div>
  );
};

// Main Application Content with Routing
const AppContent: React.FC = () => {
  // Removed isLoading check here - components handle their own loading states
  // This prevents unmounting the entire app when auth operations are in progress

  return (
    <Router>
      {/* T-045 (11.09.2026): laedt die Seite automatisch neu, sobald der Server ein neueres Bundle liefert */}
      <VersionWatcher />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <AuthContainer>
              <LoginForm />
            </AuthContainer>
          } 
        />
        <Route
          path="/register"
          element={
            <AuthContainer>
              <RegisterForm />
            </AuthContainer>
          }
        />

        {/* Email Verification - Phase 2 */}
        <Route
          path="/verify-email"
          element={<VerifyEmail />}
        />

        {/* Passwort vergessen / zurücksetzen (Befund B5, 18.08.2026) - Public Routes */}
        <Route
          path="/passwort-vergessen"
          element={<ForgotPasswordPage />}
        />
        <Route
          path="/passwort-zuruecksetzen"
          element={<ResetPasswordPage />}
        />

        {/* Voucher Purchase - Public Routes */}
        <Route
          path="/gutschein-kaufen"
          element={<VoucherPurchase />}
        />
        <Route
          path="/gutschein-kaufen/success"
          element={<VoucherSuccess />}
        />
        <Route
          path="/gutschein-kaufen/vorkasse"
          element={<VoucherBankTransfer />}
        />

        {/* Quote Acceptance - Public Route (login handled within page) */}
        <Route
          path="/angebot-annehmen/:bookingId"
          element={<QuoteAcceptPage />}
        />

        {/* Root Redirect - Login-First Architecture */}
        <Route
          path="/"
          element={<RootRedirect />}
        />

        {/* Protected Routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        
        {/* T-115 (v1.8.31, 27.09.2026): Buchungskalender ohne Anmeldung sichtbar (öffentliche Terminansicht,
            Kundin 26.09.2026). Buchen und Anfragen verlangen weiterhin die Anmeldung (im Kalender geprüft,
            serverseitig ohnehin [Authorize]). */}
        <Route
          path="/booking"
          element={
            <MainLayout>
              <BookingCalendarRoute />
            </MainLayout>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Booking Confirmation - Phase 3 */}
        <Route
          path="/booking-confirmation/:bookingId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <BookingConfirmation />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Booking Payment Success - PayPal Return URL */}
        <Route
          path="/buchung/success"
          element={
            <ProtectedRoute>
              <MainLayout>
                <BookingPaymentSuccess />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="Admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="bookings" element={<BookingManagement />} />
          <Route path="quotes" element={<QuoteManagement />} />
          <Route path="booking-new" element={<BookingFlow />} />
          <Route path="customers" element={<CustomerManagement />} />
          <Route path="calendar" element={<CalendarManagement />} />
          <Route path="addons" element={<AddOnManagement />} />
          <Route path="tour-types" element={<TourTypeManagement />} />
          <Route path="tour-type-config" element={<TourTypeAddonConfig />} />
          {/* T-046 (11.09.2026): "payments" (Zahlungen) und "reports" (Berichte) ausgeblendet
              (Prototypen, nicht Teil der Anlage v1.4, Entscheidung nach GoLive T-048), "documents"
              (Platzhalter "Coming Soon") entfernt. Alte Links landen ueber die Catch-all-Route im Dashboard. */}
          <Route path="invoices" element={<InvoiceManagement />} />
          {/* Barrechnungen und Bordverkäufe (Anlage v1.4 Abschnitt 2.7 Punkt 10) */}
          <Route path="cash-invoices" element={<CashInvoiceManagement />} />
          {/* Bankzahlungsabgleich, Erststufe CSV (Anlage v1.4 Abschnitt 2.5 Punkt 10) */}
          <Route path="bank-reconciliation" element={<BankReconciliation />} />
          <Route path="vouchers" element={<VoucherManagement />} />
          <Route path="email-queue" element={<EmailQueueManagement />} />
          <Route path="settings" element={<Settings />} />
          {/* T-046: Detail-Links auf /admin/bookings/{id} (Dashboard) gab es als Route nie, jetzt zur Buchungsliste */}
          <Route path="bookings/:bookingId" element={<Navigate to="/admin/bookings" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
        
        <Route 
          path="/checkout/:bookingId" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <Checkout />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/payment/success" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <PaymentSuccess />
              </MainLayout>
            </ProtectedRoute>
          } 
        />

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <PaymentProvider>
        <div className="App">
          <AppContent />
        </div>
      </PaymentProvider>
    </AuthProvider>
  );
};

export default App;
